import { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  FieldGroup,
  FormRow,
  RadioCard,
  TextField,
  Textarea,
} from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { isNextStepRequired } from '../tasks/api';
import { addDays, isValidDayKey, isValidTime, todayKey } from '../tasks/dates';
import { useCancelAppointment, type AppointmentDetail } from './api';
import styles from './CancelDialog.module.css';

export interface CancelDialogProps {
  open: boolean;
  appointment: AppointmentDetail;
  onClose: () => void;
  onCancelled: (updated: AppointmentDetail) => void;
}

const REASON_REQUIRED = 'Bitte geben Sie einen Grund an.';
const DATE_REQUIRED = 'Bitte ein Datum wählen.';
const TIME_INVALID = 'Bitte eine Uhrzeit im Format HH:MM angeben.';

/**
 * „Absagen" (T6/L8): who cancelled, the mandatory reason, and the follow-up the contact needs when the
 * advisor cancels (`409 NEXT_STEP_REQUIRED` switches the follow-up on).
 */
export function CancelDialog({ open, appointment, onClose, onCancelled }: CancelDialogProps) {
  const cancel = useCancelAppointment();
  const today = todayKey();
  const [cancelledBy, setCancelledBy] = useState<'customer' | 'advisor'>('customer');
  const [reason, setReason] = useState('');
  const [withFollowUp, setWithFollowUp] = useState(false);
  const [dueOn, setDueOn] = useState(() => addDays(todayKey(), 1));
  const [dueTime, setDueTime] = useState('09:00');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [needsStep, setNeedsStep] = useState(false);
  const resetMutation = cancel.reset;

  useEffect(() => {
    if (!open) return;
    setCancelledBy('customer');
    setReason('');
    setWithFollowUp(false);
    setDueOn(addDays(todayKey(), 1));
    setDueTime('09:00');
    setNote('');
    setErrors({});
    setNeedsStep(false);
    resetMutation();
  }, [open, resetMutation]);

  const serverFields = cancel.error instanceof ApiError ? cancel.error.fields : {};
  const fieldError = (key: string) => errors[key] ?? serverFields[key];

  function submit() {
    const next: Record<string, string> = {};
    if (reason.trim() === '') next['reason'] = REASON_REQUIRED;
    if (withFollowUp) {
      if (!isValidDayKey(dueOn)) next['dueOn'] = DATE_REQUIRED;
      if (dueTime !== '' && !isValidTime(dueTime)) next['dueTime'] = TIME_INVALID;
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    cancel.mutate(
      {
        id: appointment.id,
        body: {
          reason: reason.trim(),
          cancelledBy,
          ...(withFollowUp
            ? {
                followUp: {
                  dueOn,
                  ...(dueTime !== '' ? { dueTime } : {}),
                  ...(note.trim() !== '' ? { note: note.trim() } : {}),
                },
              }
            : {}),
        },
      },
      {
        onSuccess: onCancelled,
        onError: (error) => {
          if (isNextStepRequired(error)) {
            setNeedsStep(true);
            setWithFollowUp(true);
          }
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      eyebrow={appointment.eventType.name}
      title="Termin absagen"
      subtitle={appointment.timeRangeText}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={cancel.isPending}>
            Abbrechen
          </Button>
          <Button variant="destructiveFilled" loading={cancel.isPending} onClick={submit}>
            Absagen
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {cancel.isError && !isNextStepRequired(cancel.error) && Object.keys(serverFields).length === 0 ? (
          <Banner tone="danger">{errorMessage(cancel.error)}</Banner>
        ) : null}
        {needsStep ? (
          <Banner tone="warning">
            Der Kontakt braucht einen nächsten Schritt. Legen Sie ein Follow-up an, damit die Absage
            gespeichert werden kann.
          </Banner>
        ) : null}
        <FieldGroup legend="Abgesagt durch" columns={2}>
          <RadioCard
            name="cancelled-by"
            value="customer"
            title="Kunde"
            description="Follow-up geht an Setter oder Berater"
            checked={cancelledBy === 'customer'}
            onChange={() => setCancelledBy('customer')}
          />
          <RadioCard
            name="cancelled-by"
            value="advisor"
            title="Berater"
            description="Der Kontakt braucht einen nächsten Schritt"
            checked={cancelledBy === 'advisor'}
            onChange={() => setCancelledBy('advisor')}
          />
        </FieldGroup>
        <Textarea
          label="Grund"
          required
          rows={3}
          autoFocus
          value={reason}
          error={fieldError('reason')}
          onChange={(event) => setReason(event.target.value)}
        />
        <Checkbox
          label="Follow-up anlegen"
          description="Aufgabe für den Kontakt nach der Absage"
          checked={withFollowUp}
          onChange={(event) => setWithFollowUp(event.target.checked)}
        />
        {withFollowUp ? (
          <>
            <FormRow columns={2}>
              <TextField
                label="Fällig am"
                type="date"
                required
                min={today}
                value={dueOn}
                error={fieldError('dueOn')}
                onChange={(event) => setDueOn(event.target.value)}
              />
              <TextField
                label="Uhrzeit"
                type="time"
                value={dueTime}
                error={fieldError('dueTime')}
                onChange={(event) => setDueTime(event.target.value)}
              />
            </FormRow>
            <TextField
              label="Notiz"
              optional
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
